import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  ButtonBase,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { IconCheck, IconChevronDown, IconCopy, IconSearch } from "@tabler/icons";
import { copyText } from "./api";
import { answerForReading } from "./format";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import { fieldAnchor, officialAnchor, readHideNotAsked, reveal, saveHideNotAsked } from "./workspace";

// The copy icon on its own. "Copied" shows only after the copy worked.
export function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Copied" : `Copy ${label}`}>
      <IconButton
        size="small"
        aria-label={`Copy ${label}`}
        onClick={async (e) => {
          e.stopPropagation();
          if (await copyText(String(value))) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
          }
        }}
        // A small icon, a 44px target on touch (QA nadia-25): 26px + 9px each side.
        sx={[{ color: copied ? "success.main" : "text.secondary" }, iconButtonSx, { "@media (pointer: coarse)": { "&::after": { inset: -9 } } }]}
      >
        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
      </IconButton>
    </Tooltip>
  );
}

CopyButton.propTypes = { value: PropTypes.string.isRequired, label: PropTypes.string.isRequired };

// A value with its copy button.
export function CopyValue({ value, label, mono }) {
  if (value == null || value === "") return null;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography
        sx={{ fontWeight: 600, wordBreak: "break-word", fontFamily: mono ? "monospace" : undefined }}
      >
        {value}
      </Typography>
      <CopyButton value={String(value)} label={label} />
    </Stack>
  );
}

CopyValue.propTypes = { value: PropTypes.string, label: PropTypes.string, mono: PropTypes.bool };

const MONO = new Set(["passport_number", "national_id_number", "phone", "previous_visa_sticker_number"]);

// A part of an official field is "asked" when this visa has a question for it.
const isAsked = (value) => !!value.question;
const itemAsked = (item) => item.values.some(isAsked);

// One official field: its number, the form's own words, and each part's value.
function OfficialRow({ item, anchor, hideNotAsked, requiredKeys }) {
  const values = hideNotAsked && itemAsked(item) ? item.values.filter(isAsked) : item.values;
  return (
    <Box
      id={anchor}
      tabIndex={-1}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "36px 1fr", sm: "44px minmax(0, 1fr) minmax(0, 1.1fr)" },
        gap: { xs: 1, sm: 2 },
        py: 1.5,
        px: 1,
        mx: -1,
        borderBottom: 1,
        borderColor: "divider",
        borderRadius: 1,
        scrollMarginTop: 160,
        "&:focus": { outline: "none" },
      }}
    >
      <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "primary.main" }}>{item.number}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2">{item.label}</Typography>
        {item.skip_for_eu_family && (
          <Typography variant="caption" color="text.secondary">
            Skipped by family members of EU, EEA or Swiss citizens
          </Typography>
        )}
      </Box>
      <Stack spacing={0.75} sx={{ gridColumn: { xs: "2", sm: "auto" }, minWidth: 0 }}>
        {values.map((v) => (
          <Box key={v.key}>
            {item.values.length > 1 && (
              <Typography variant="caption" color="text.secondary">
                {v.label}
              </Typography>
            )}
            {v.value != null ? (
              <CopyValue value={v.value} label={v.label} mono={MONO.has(v.key)} />
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {v.question ? "Not answered" : "Not asked by this visa"}
                </Typography>
                {v.question && requiredKeys.has(v.key) && <Chip size="small" color="warning" label="Missing" />}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

OfficialRow.propTypes = {
  item: PropTypes.object.isRequired,
  anchor: PropTypes.string,
  hideNotAsked: PropTypes.bool,
  requiredKeys: PropTypes.instanceOf(Set).isRequired,
};

// Everything in official order as plain text, for a note or an email.
function officialAsText(traveller) {
  const lines = traveller.official.items.map((item) => {
    const parts = item.values.map((v) => (item.values.length > 1 ? `${v.label}: ${v.value ?? "—"}` : v.value ?? "—"));
    return `${item.number}. ${item.label}: ${parts.join(" · ")}`;
  });
  const other = traveller.official.other.map((f) => `${f.label}: ${f.value}`);
  return [...lines, ...(other.length ? ["", ...other] : [])].join("\n");
}

// "15" finds field 15 first, then 15x; words match the form's label, a
// part's name or the question the customer was asked.
function findFields(items, { inputValue }) {
  const q = inputValue.trim().toLowerCase();
  if (!q) return items;
  if (/^\d+$/.test(q)) {
    const exact = items.filter((item) => item.number === q);
    const starts = items.filter((item) => item.number !== q && item.number.startsWith(q));
    return [...exact, ...starts];
  }
  return items.filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.values.some((v) => String(v.label || "").toLowerCase().includes(q) || String(v.question || "").toLowerCase().includes(q)),
  );
}

// A group of answers, folded until opened (QA nadia-06): its title, how
// many answers and what is missing show on the folded line.
function AnswerGroup({ section, index, open, onToggle, requestId }) {
  const id = `case-answers-${requestId}-${index}`;
  const missing = section.fields.filter((f) => f.required && f.value == null).length;
  return (
    <Box mb={open ? 2 : 0} sx={{ borderBottom: open ? 0 : 1, borderColor: "divider" }}>
      <Typography component="h4" variant="subtitle2" sx={{ m: 0 }}>
        <ButtonBase
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={id}
          sx={{
            width: "100%",
            minHeight: 44,
            justifyContent: "space-between",
            gap: 1,
            px: 1,
            mx: -1,
            borderRadius: 1,
            textAlign: "left",
            "&.Mui-focusVisible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: "-2px" },
          }}
        >
          <Box component="span" sx={{ textTransform: "uppercase", color: "text.secondary", fontWeight: 600, fontSize: "0.8125rem" }}>
            {section.title}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" component="span" sx={{ flexShrink: 0 }}>
            {missing > 0 && <Chip size="small" color="warning" label={`${missing} missing`} component="span" />}
            <Box component="span" sx={{ color: "text.secondary", fontSize: "0.8125rem" }}>
              {section.fields.length === 1 ? "1 answer" : `${section.fields.length} answers`}
            </Box>
            <IconChevronDown size={18} aria-hidden style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 120ms" }} />
          </Stack>
        </ButtonBase>
      </Typography>
      {open ? (
        <Box id={id}>
          {section.fields.map((f) => (
            <Stack
              key={f.question_id}
              id={fieldAnchor(requestId, f.question_id)}
              tabIndex={-1}
              direction={{ xs: "column", sm: "row" }}
              spacing={{ sm: 2 }}
              py={1}
              px={1}
              mx={-1}
              borderBottom={1}
              borderColor="divider"
              sx={{ borderRadius: 1, scrollMarginTop: 160, "&:focus": { outline: "none" } }}
            >
              <Typography variant="body2" sx={{ flex: 1 }}>
                {f.label}
                {f.required && f.value == null && <Chip size="small" color="warning" label="Missing" sx={{ ml: 1 }} />}
              </Typography>
              <Box sx={{ flex: 1.1, minWidth: 0 }}>
                {f.value != null ? (
                  // Dates for reading here ("10 Dec 2026"); the official
                  // form's order keeps DD/MM/YYYY to type in (QA nadia-26).
                  <CopyValue value={answerForReading(f.value, f.type)} label={f.label} mono={MONO.has(f.key)} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Box>
            </Stack>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

AnswerGroup.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  requestId: PropTypes.number,
};

export default function Answers({ traveller, officialTitle, jump }) {
  const hasOfficial = !!traveller.official;
  const requestId = traveller.request_id;
  const [mode, setMode] = useState(hasOfficial ? "official" : "answered");
  // Groups start folded, except one with a required answer missing.
  const [openGroups, setOpenGroups] = useState(
    () => new Set(traveller.sections.map((section, index) => (section.fields.some((f) => f.required && f.value == null) ? index : null)).filter((i) => i != null)),
  );
  const allOpen = traveller.sections.length > 0 && openGroups.size === traveller.sections.length;
  const toggleGroup = (index) =>
    setOpenGroups((all) => {
      const next = new Set(all);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  const [copiedAll, setCopiedAll] = useState(false);
  const [hideNotAsked, setHideNotAsked] = useState(readHideNotAsked);
  const [revealed, setRevealed] = useState(null);
  const [findText, setFindText] = useState("");
  const [target, setTarget] = useState(null);
  const missing = traveller.missing.answers;

  const items = useMemo(() => traveller.official?.items || [], [traveller.official]);
  const requiredKeys = useMemo(
    () => new Set(traveller.sections.flatMap((s) => s.fields).filter((f) => f.required && f.key).map((f) => f.key)),
    [traveller.sections],
  );
  const shownItems = items.filter((item) => !hideNotAsked || itemAsked(item) || item.number === revealed);
  const hiddenCount = items.length - shownItems.length;

  // A check's "Go to" for an answer: the official row when the field is on
  // the form and that view is showing, otherwise the customer's own order.
  useEffect(() => {
    if (!jump || jump.type !== "field" || jump.requestId !== requestId) return;
    const item = jump.key ? items.find((i) => i.values.some((v) => v.key === jump.key)) : null;
    if (mode === "official" && item) {
      setRevealed(item.number);
      setTarget({ id: officialAnchor(requestId, item.number), n: jump.n });
    } else {
      setMode("answered");
      // Open the group the answer is in before going to it.
      const group = traveller.sections.findIndex((section) => section.fields.some((f) => Number(f.question_id) === Number(jump.questionId)));
      if (group >= 0) setOpenGroups((all) => new Set([...all, group]));
      setTarget({ id: fieldAnchor(requestId, jump.questionId), n: jump.n });
    }
    // Only a new jump moves the view; switching modes by hand must not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jump]);

  useEffect(() => {
    if (!target) return undefined;
    const frame = requestAnimationFrame(() => reveal(target.id));
    return () => cancelAnimationFrame(frame);
  }, [target]);

  const goToItem = (item) => {
    if (!item) return;
    if (!itemAsked(item)) setRevealed(item.number);
    setFindText("");
    setTarget({ id: officialAnchor(requestId, item.number), n: Date.now() });
  };

  return (
    <Box>
      {/* The toggle replaces the visible title; the outline keeps "Answers". */}
      {hasOfficial && (
        <Typography component="h3" sx={visuallyHidden}>
          Answers
        </Typography>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} mb={1}>
        {hasOfficial ? (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, next) => next && setMode(next)}
            aria-label="How to show the answers"
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 1.75, minHeight: 40 } }}
          >
            <ToggleButton value="official">Official form order</ToggleButton>
            <ToggleButton value="answered">As the customer answered</ToggleButton>
          </ToggleButtonGroup>
        ) : (
          <Typography variant="h6" component="h3">
            Answers
          </Typography>
        )}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {missing > 0 && <Chip size="small" color="warning" label={missing === 1 ? "1 required answer missing" : `${missing} required answers missing`} />}
          {mode === "answered" && traveller.sections.length > 1 && (
            <ActionButton variant="text" onClick={() => setOpenGroups(allOpen ? new Set() : new Set(traveller.sections.map((_, i) => i)))} aria-expanded={allOpen}>
              {allOpen ? "Fold all" : "Show all answers"}
            </ActionButton>
          )}
          {mode === "official" && hasOfficial && (
            <ActionButton
              variant="text"
              startIcon={copiedAll ? <IconCheck size={16} /> : <IconCopy size={16} />}
              onClick={async () => {
                if (await copyText(officialAsText(traveller))) {
                  setCopiedAll(true);
                  setTimeout(() => setCopiedAll(false), 1600);
                }
              }}
            >
              {copiedAll ? "Copied" : "Copy all as text"}
            </ActionButton>
          )}
        </Stack>
      </Stack>

      {mode === "official" && hasOfficial && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            {officialTitle}: fill the official form top to bottom with these values. Dates are DD/MM/YYYY.
          </Typography>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1, md: 2 }}
            useFlexGap
            alignItems={{ md: "center" }}
            sx={{ mb: 1, p: 1.5, borderRadius: 2, bgcolor: "grey.100", border: 1, borderColor: "divider" }}
          >
            <Autocomplete
              size="small"
              options={items}
              value={null}
              inputValue={findText}
              onInputChange={(_, text, reason) => reason !== "reset" && setFindText(text)}
              onChange={(_, item) => goToItem(item)}
              filterOptions={findFields}
              getOptionLabel={(item) => `${item.number}. ${item.label}`}
              isOptionEqualToValue={(a, b) => a.number === b.number}
              autoHighlight
              clearOnBlur
              noOptionsText="No field matches"
              sx={{ flex: 1, minWidth: { md: 260 }, maxWidth: { md: 380 }, bgcolor: "background.paper", borderRadius: 1 }}
              renderOption={(props, item) => (
                <li {...props} key={item.number}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "32px 1fr", gap: 1, width: "100%" }}>
                    <Typography sx={{ fontFamily: "monospace", fontWeight: 700, color: "primary.main" }}>{item.number}</Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2">{item.label}</Typography>
                      {!itemAsked(item) && (
                        <Typography variant="caption" color="text.secondary">
                          Not asked by this visa
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Go to a field"
                  inputProps={{ ...params.inputProps, "aria-label": "Go to a field of the official form" }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconSearch size={16} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <FormControlLabel
              sx={{ mr: 0, ml: { xs: -0.5, md: 0 } }}
              control={
                <Switch
                  checked={hideNotAsked}
                  onChange={(e) => {
                    setHideNotAsked(e.target.checked);
                    saveHideNotAsked(e.target.checked);
                    setRevealed(null);
                  }}
                />
              }
              label={<Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>Only fields this visa asks</Typography>}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: { md: "auto" }, whiteSpace: "nowrap" }}>
              {hiddenCount > 0 ? `${shownItems.length} of ${items.length} fields shown` : `All ${items.length} fields`}
            </Typography>
          </Stack>
          {shownItems.map((item) => (
            <OfficialRow
              key={item.number}
              item={item}
              anchor={officialAnchor(requestId, item.number)}
              hideNotAsked={hideNotAsked}
              requiredKeys={requiredKeys}
            />
          ))}
          {traveller.official.other.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" component="h4" mb={0.5}>
                Other answers (no field on the official form)
              </Typography>
              {traveller.official.other.map((f) => (
                <Stack key={f.question_id} direction={{ xs: "column", sm: "row" }} spacing={{ sm: 2 }} py={1} borderBottom={1} borderColor="divider">
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {f.label}
                  </Typography>
                  <Box sx={{ flex: 1.1, minWidth: 0 }}>
                    <CopyValue value={f.value} label={f.label} />
                  </Box>
                </Stack>
              ))}
            </Box>
          )}
        </Box>
      )}

      {mode === "answered" &&
        traveller.sections.map((section, index) => (
          <AnswerGroup key={`${index}-${section.title}`} section={section} index={index} open={openGroups.has(index)} onToggle={() => toggleGroup(index)} requestId={requestId} />
        ))}
    </Box>
  );
}

Answers.propTypes = { traveller: PropTypes.object.isRequired, officialTitle: PropTypes.string, jump: PropTypes.object };
